import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { usePopper } from 'react-popper';
import { primaryColors } from '../../theme';
import { SiteContext } from '../../context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';

const StyledThemePicker = styled.div`
  margin-left: 10px;
  display: flex;
  border: none;
  justify-content: flex-end;
  button + button {
    margin-left: 10px;
  }
`;

const StyledColorPicker = styled.div`
  background-color: #efefef;
  padding: 20px;
  border-radius: 20px;
  display: flex;
  z-index: 10;
`;
const StyledColorPickerButton = styled.button`
  background-color: transparent;
  border: none;
  height: 40px;
  font-size: 1.2rem;
  svg {
    color: ${({ theme }) => theme.text};
  }
`;
const ThemePicker = () => {
  const { globalState, dispatch } = useContext(SiteContext);
  const buttonRef = useRef<HTMLButtonElement>(
    ('' as unknown) as HTMLButtonElement
  );
  const menuRef = useRef<HTMLDivElement>(('' as unknown) as HTMLDivElement);
  const [
    referenceElement,
    setReferenceElement,
  ] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  const [colorPickerVisible, setColorPickerVisible] = useState(false);

  function findUpTag(el: any, tag: any) {
    while (el.parentNode) {
      el = el.parentNode;
      if (el === tag) return el;
    }
    return null;
  }

  const handleClickOutside = (e: MouseEvent): void => {
    const parentOfMenu = findUpTag(e.target, menuRef.current);
    const parentOfButton = findUpTag(e.target, buttonRef.current);
    if (!parentOfMenu && !parentOfButton && e.target !== buttonRef.current) {
      setColorPickerVisible(false);
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const colors = [
    { color: '#266D78', name: 'default' },
    { color: '#C93131', name: 'red' },
    { color: '#464646', name: 'gray' },
    { color: '#8131c9', name: 'purple' },
    { color: '#3160c9', name: 'blue' },
  ];
  return (
    <StyledThemePicker>
      <div ref={setReferenceElement}>
        <StyledColorPickerButton
          ref={buttonRef}
          onClick={() => setColorPickerVisible(!colorPickerVisible)}
        >
          <FontAwesomeIcon icon={faPalette} />
        </StyledColorPickerButton>
      </div>

      {colorPickerVisible && (
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <StyledColorPicker ref={menuRef}>
            {colors.map((color: any) => {
              return (
                <button
                  title={`Switch to ${color.name}`}
                  key={color.color}
                  onClick={() => {
                    setColorPickerVisible(false);
                    dispatch({
                      type: 'SET_THEME',
                      payload: color.name,
                    });
                    window.localStorage.setItem(
                      'stori_theme_color',
                      color.name
                    );
                    const root = window.document.documentElement;
                    root.style.setProperty(
                      '--color-primary',
                      globalState.themeMode === 'dark'
                        ? primaryColors[color.name].dark.primary
                        : primaryColors[color.name].light.primary
                    );
                    root.style.setProperty(
                      '--color-button-text-primary',
                      globalState.themeMode === 'dark'
                        ? primaryColors[color.name].dark.primaryButtonText
                        : primaryColors[color.name].light.primaryButtonText
                    );
                  }}
                  style={{
                    backgroundColor: color.color,
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: 'none',
                  }}
                />
              );
            })}
          </StyledColorPicker>
        </div>
      )}
    </StyledThemePicker>
  );
};

export default ThemePicker;
